import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Star } from 'lucide-react';
import { Card } from '../ui/Card';
import { PhotoCarousel } from './PhotoCarousel';
import { severityLabels } from './StarRating';
interface Report {
  id: string;
  type: string;
  emoji: string;
  isBlocking: boolean;
  reportedBy: {
    name: string;
    role: string;
    avatar?: string;
  };
  timestamp: string;
  area: string;
  severity: number; // Cambiato da 'low' | 'medium' | 'high' a number (1-5)
  description: string;
  photos: string[];
}
interface ReportCardProps {
  report: Report;
  isExpanded: boolean;
  onToggle: () => void;
  forceExpanded?: boolean;
}
export function ReportCard({
  report,
  isExpanded,
  onToggle,
  forceExpanded = false
}: ReportCardProps) {
  const severityInfo = severityLabels[report.severity];
  return <Card className="overflow-hidden" data-id="element-197">
      <button onClick={onToggle} disabled={forceExpanded} className={`w-full p-8 flex items-center justify-between transition-colors touch-manipulation ${!forceExpanded ? 'hover:bg-gray-50' : ''}`} data-id="element-198">
        <div className="flex items-center gap-4 flex-1 text-left" data-id="element-199">
          <span className="text-4xl" data-id="element-200">{report.emoji}</span>
          <div className="flex-1 min-w-0" data-id="element-201">
            <div className="flex items-center gap-2 flex-wrap mb-2" data-id="element-202">
              <span className="text-xl font-semibold text-navy" data-id="element-203">
                {report.type}
              </span>
              {report.isBlocking && <span className="px-4 py-1.5 bg-red-100 text-red-700 rounded-full text-base font-semibold border border-red-200" data-id="element-204">
                  Bloccante
                </span>}
            </div>
            <p className="text-lg text-gray-500" data-id="element-205">{report.area}</p>
          </div>
        </div>
        {!forceExpanded && <motion.div animate={{
        rotate: isExpanded ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-206">
            <ChevronDown className="w-7 h-7 text-gray-400" data-id="element-207" />
          </motion.div>}
      </button>

      <AnimatePresence data-id="element-208">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-209">
            <div className="px-8 pb-8 pt-4 border-t border-gray-100 space-y-6" data-id="element-210">
              {/* Segnalato da - SENZA AVATAR */}
              <div data-id="element-211">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-212">
                  Segnalato da
                </p>
                <div data-id="element-213">
                  <p className="text-lg font-medium text-navy" data-id="element-214">
                    {report.reportedBy.name}
                  </p>
                  <p className="text-base text-gray-500" data-id="element-215">
                    {report.reportedBy.role}
                  </p>
                </div>
              </div>

              {/* Timestamp */}
              <div data-id="element-216">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-217">
                  Orario
                </p>
                <p className="text-lg text-gray-700" data-id="element-218">{report.timestamp}</p>
              </div>

              {/* Gravità - STAR RATING */}
              <div data-id="element-219">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-220">
                  Gravità
                </p>
                <div className="flex items-center gap-3" data-id="element-221">
                  <div className="flex gap-1" data-id="element-222">
                    {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`w-6 h-6 ${star <= report.severity ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} strokeWidth={2} data-id="element-223" />)}
                  </div>
                  <span className={`text-lg font-semibold ${severityInfo?.color}`} data-id="element-224">
                    {severityInfo?.label}
                  </span>
                </div>
              </div>

              {/* Descrizione - FONT GRANDE */}
              <div data-id="element-225">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-226">
                  Descrizione
                </p>
                <p className="text-lg text-gray-700 leading-relaxed bg-gray-50 p-5 rounded-lg" data-id="element-227">
                  {report.description}
                </p>
              </div>

              {/* Foto - THUMBNAILS CON GALLERY */}
              {report.photos.length > 0 && <div data-id="element-228">
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-229">
                    Foto
                  </p>
                  <PhotoCarousel photos={report.photos} data-id="element-230" />
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </Card>;
}